/**
 * Giftera × Ella — giftera-product.js (doar pe pagina de produs, încărcat cu defer din
 * sections/giftera-product.liquid).
 *
 * Vanilla, fără dependențe. Din Ella se folosește doar API-ul public:
 *   Shopify.getCart(cb), window.sharedFunctions.updateSidebarCart(cart), clasa `cart-sidebar-show`.
 *
 * Ce face (fiecare parte nu face nimic dacă elementele ei lipsesc):
 *   - trimiterea formularului de produs (docs/SPEC-PDP.md §1, pașii 1–7), cu cele două butoane:
 *     „Adaugă în coș” (deschide coșul lateral) și „Comandă acum” (același flux, apoi merge la checkout);
 *   - galeria: schimbul pozei principale, miniatura lănțișorului ales, zoom la hover;
 *   - seturile cu variante reale („Model colier”): modelul ales în selector devine varianta din formular
 *     (initVariants), cu titlul, poza, adresa și linkul de trimis ținute la zi;
 *   - cantitatea (− / +);
 *   - bara lipicioasă de cumpărare;
 *   - „Trimite” (Web Share API; altfel copiază linkul).
 *
 * Contract cu restul paginii:
 *   - window.Giftera.afterSetAdded: array de funcții `async ({ form, quantity, item }) => {}`,
 *     rulate pe rând, cu await, după ce setul a intrat în coș (aici se agață add-on-ul cardului
 *     personalizat); erorile lor se prind și nu opresc fluxul;
 *   - evenimente pe document: `gf:set-added` ({ form, quantity, item, partial }) și
 *     `gf:set-add-failed` ({ form, quantity, message, status }); la o adăugare parțială (422, dar o
 *     parte din cantitate a intrat în coș) `quantity` e cantitatea intrată, `item` e null, `partial` true;
 *   - designerul de card ascultă `submit` în faza de CAPTURĂ pe același formular și poate opri
 *     trimiterea (preventDefault); listenerul de aici e în faza BUBBLE și iese dacă a fost oprită.
 *
 *   - „Comandă acum” e un al doilea buton submit al aceluiași formular ([data-gf-buy-now]). Care buton a
 *     trimis se citește din `event.submitter` în faza bubble: designerul retrimite cu
 *     form.requestSubmit(submitter), deci informația supraviețuiește și celei de-a doua treceri. Plasă
 *     pentru browserele fără `submitter`: ultimul buton apăsat (click), ținut minte aici.
 *   - window.Giftera.navigate(url): singurul loc prin care pagina pleacă spre checkout (se poate
 *     înlocui în teste).
 *   - `gf:variant-change` pe secțiune ([data-gf-product]), doar la seturile cu variante reale:
 *     detail = { code, variant, show }; galeria își schimbă miniatura variantei și, la `show`, poza principală.
 *     Restul paginii ascultă în continuare `change` pe input[data-gf-value], ca până acum.
 *
 * Nu expune nimic global în afară de window.Giftera.afterSetAdded și window.Giftera.navigate.
 */
(function () {
  'use strict';

  /** @type {{ afterSetAdded?: Array<Function>, toast?: (text: string, duration?: number) => void, navigate?: (url: string) => void }} */
  const Giftera = (window.Giftera = window.Giftera || {});
  if (!Array.isArray(Giftera.afterSetAdded)) Giftera.afterSetAdded = [];
  if (typeof Giftera.navigate !== 'function') {
    /** @param {string} url */
    Giftera.navigate = function (url) {
      window.location.assign(url);
    };
  }

  const ZOOM_SCALE = 1.8;
  // aceleași lățimi ca în snippets/giftera-pdp-gallery.liquid (poza principală) și miniaturi / bara lipicioasă
  const MAIN_WIDTHS = [360, 540, 720, 900, 1080, 1320, 1600];
  const THUMB_WIDTHS = [76, 152, 228];
  const STICKY_WIDTHS = [52, 104, 156];
  const initialised = new WeakSet();

  /* ---------------------------------------------------------------------- */
  /* utilitare                                                               */
  /* ---------------------------------------------------------------------- */

  /**
   * Controalele legate de formular prin atributul `form=` stau în afara <form>-ului: le căutăm
   * întâi printre elementele formularului, apoi în secțiune.
   * @param {HTMLElement} root
   * @param {HTMLFormElement | null} form
   * @param {string} selector
   * @returns {HTMLElement | null}
   */
  function findControl(root, form, selector) {
    if (form) {
      for (const el of Array.from(form.elements)) {
        if (el.matches(selector)) return /** @type {HTMLElement} */ (el);
      }
    }
    return root.querySelector(selector);
  }

  /** @param {string} text */
  function toast(text) {
    if (text && typeof Giftera.toast === 'function') Giftera.toast(text);
  }

  /**
   * Aceeași poză Shopify la altă lățime: schimbă parametrul `width` pus de filtrul image_url.
   * Întoarce '' dacă adresa nu are parametrul (atunci rămâne doar adresa primită).
   * @param {string} src
   * @param {number} width
   */
  function atWidth(src, width) {
    const pattern = /([?&]width=)\d+/;
    return pattern.test(src) ? src.replace(pattern, '$1' + width) : '';
  }

  /**
   * @param {string} src
   * @param {number[]} widths
   * @param {number} [max] - lățimea pozei originale; peste ea nu se cere nimic
   */
  function srcsetFor(src, widths, max) {
    const parts = [];
    for (const width of widths) {
      if (max && width > max) continue;
      const url = atWidth(src, width);
      if (url) parts.push(url + ' ' + width + 'w');
    }
    return parts.join(', ');
  }

  /* ---------------------------------------------------------------------- */
  /* trimiterea formularului (SPEC-PDP §1)                                   */
  /* ---------------------------------------------------------------------- */

  /**
   * Pasul 6: reîmprospătează și deschide coșul lateral Ella; fără API-ul Ella, merge la pagina de coș.
   * @param {string} cartUrl
   */
  function openCart(cartUrl) {
    const shopify = window.Shopify;
    const shared = window.sharedFunctions;

    if (shopify && typeof shopify.getCart === 'function' && shared && typeof shared.updateSidebarCart === 'function') {
      shopify.getCart(function (cart) {
        document.body.classList.add('cart-sidebar-show');
        shared.updateSidebarCart(cart);
      });
      return;
    }

    window.location.assign(cartUrl || '/cart');
  }

  /**
   * Câte bucăți din varianta dată sunt acum în coș (pe toate liniile ei); null dacă nu se poate afla.
   * @param {string} base - rădăcina rutelor (Shopify.routes.root)
   * @param {string} variantId
   * @returns {Promise<number | null>}
   */
  async function variantQuantity(base, variantId) {
    if (!variantId) return null;
    try {
      const response = await fetch(base + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) return null;
      const cart = await response.json();
      const items = cart && Array.isArray(cart.items) ? cart.items : [];
      return items.reduce(function (sum, item) {
        return String(item.variant_id) === variantId ? sum + (Number(item.quantity) || 0) : sum;
      }, 0);
    } catch (error) {
      return null;
    }
  }

  /**
   * @param {HTMLElement} root - secțiunea [data-gf-product]
   * @param {HTMLFormElement} form - [data-gf-product-form]
   */
  function initForm(root, form) {
    const addButton = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-gf-add]'));
    const nowButton = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-gf-buy-now]'));
    const buttons = /** @type {HTMLButtonElement[]} */ ([addButton, nowButton].filter(Boolean));
    const errorEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-gf-form-error]'));
    const fallbackError = root.getAttribute('data-error-text') || '';
    // stoc epuizat: butoanele vin dezactivate din Liquid și rămân așa
    const soldOut = !!addButton && addButton.disabled;
    /** @type {Map<HTMLButtonElement, string>} */
    const idleText = new Map();
    for (const item of buttons) {
      const label = item.querySelector('[data-gf-btn-text]');
      idleText.set(item, label ? label.textContent || '' : '');
    }
    let busy = false;
    /** @type {HTMLButtonElement | null} */
    let active = null;
    // plasă pentru trimiterile fără `event.submitter`: ultimul buton apăsat
    let lastClickedNow = false;

    // semn pentru gf-card-designer.js că trimiterea e preluată de aici (altfel își adaugă singur add-on-ul)
    form.setAttribute('data-gf-ajax', '');

    if (addButton) addButton.addEventListener('click', function () { lastClickedNow = false; });
    if (nowButton) nowButton.addEventListener('click', function () { lastClickedNow = true; });

    // Cât designerul urcă poza, el dezactivează butonul principal și pe cel apăsat. Ținem „Comandă acum”
    // la fel cu „Adaugă în coș”, ca niciunul să nu poată fi apăsat în timpul unei trimiteri.
    if (addButton && nowButton && 'MutationObserver' in window) {
      new MutationObserver(function () {
        if (busy) return;
        if (nowButton.disabled !== addButton.disabled) nowButton.disabled = addButton.disabled;
      }).observe(addButton, { attributes: true, attributeFilter: ['disabled'] });
    }

    /**
     * Ambele butoane se dezactivează; starea de încărcare (aria-busy + text) apare doar pe cel apăsat.
     * @param {boolean} on
     * @param {HTMLButtonElement | null} [which] - butonul apăsat (doar la pornire)
     */
    function setBusy(on, which) {
      busy = on;
      if (on) {
        active = which || addButton;
        const hadFocus = !!active && document.activeElement === active;
        for (const item of buttons) item.disabled = true;
        if (active) {
          active.setAttribute('aria-busy', 'true');
          const label = active.querySelector('[data-gf-btn-text]');
          if (label && active.getAttribute('data-adding')) label.textContent = active.getAttribute('data-adding');
          if (hadFocus) active.setAttribute('data-gf-had-focus', '');
        }
        return;
      }

      for (const item of buttons) {
        item.removeAttribute('aria-busy');
        item.disabled = soldOut;
        const label = item.querySelector('[data-gf-btn-text]');
        if (label) label.textContent = idleText.get(item) || '';
      }
      // butonul dezactivat pierde focusul; îl dăm înapoi dacă nu l-a luat nimeni între timp
      if (active && active.hasAttribute('data-gf-had-focus')) {
        if (document.activeElement === document.body || !document.activeElement) active.focus({ preventScroll: true });
        active.removeAttribute('data-gf-had-focus');
      }
      active = null;
    }

    // Întoarcerea din checkout cu „înapoi” (pagina vine din bfcache cu butoanele încă dezactivate):
    // repornim butoanele și aducem la zi coșul lateral, fără să-l deschidem.
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      setBusy(false);
      const shopify = window.Shopify;
      const shared = window.sharedFunctions;
      if (shopify && typeof shopify.getCart === 'function' && shared && typeof shared.updateSidebarCart === 'function') {
        shopify.getCart(function (cart) { shared.updateSidebarCart(cart); });
      }
    });

    /** @param {string} message */
    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = !message;
    }

    /**
     * @param {number} quantity
     * @param {string} message
     * @param {number} status
     */
    function fail(quantity, message, status) {
      showError(message || fallbackError);
      document.dispatchEvent(
        new CustomEvent('gf:set-add-failed', { detail: { form: form, quantity: quantity, message: message || fallbackError, status: status } })
      );
      setBusy(false);
    }

    form.addEventListener('submit', async function (event) {
      // 1. designerul (faza de captură) a oprit trimiterea ca să urce poza
      if (event.defaultPrevented) return;

      // 2.
      event.preventDefault();
      if (busy) return;

      // „Comandă acum” sau „Adaugă în coș”? (bara lipicioasă trimite cu butonul principal ca submitter)
      const submitter = /** @type {HTMLElement | null} */ (event.submitter || null);
      const buyNow = !!nowButton && (submitter ? submitter === nowButton : lastClickedNow);
      lastClickedNow = false;
      setBusy(true, buyNow ? nowButton : addButton);
      showError('');

      // 3. FormData include controalele legate prin form= și sare peste cele disabled; butonul apăsat nu
      //    intră în FormData, deci `return_to` (folosit doar la POST-ul fără JS) nu ajunge la cart/add.js
      const body = new FormData(form);
      const quantity = Math.max(1, parseInt(String(body.get('quantity') || '1'), 10) || 1);
      const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const variantId = String(body.get('id') || '');

      // fotografia coșului dinaintea adăugării: la 422 Shopify poate adăuga totuși o parte din cantitate
      const before = await variantQuantity(base, variantId);

      let response;
      try {
        response = await fetch(base + 'cart/add.js', {
          method: 'POST',
          body: body,
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
        });
      } catch (error) {
        fail(quantity, '', 0);
        return;
      }

      let data = null;
      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }

      // 4. răspuns ne-OK: mesajul Shopify, fără să deschidem coșul. Excepție: adăugarea parțială (cantitatea
      //    cerută depășește stocul → Shopify pune în coș cât are ȘI răspunde 422): setul e în coș, deci
      //    merg mai departe cârligele (add-on-ul, cu cantitatea intrată efectiv) și coșul se deschide.
      let added = quantity;
      let partial = false;
      if (!response.ok) {
        const raw = (data && (data.description || data.message)) || '';
        const message = typeof raw === 'string' ? raw : '';
        const after = before === null ? null : await variantQuantity(base, variantId);
        const delta = before !== null && after !== null ? after - before : 0;
        if (!(delta > 0)) {
          fail(quantity, message, response.status);
          return;
        }
        added = delta;
        partial = true;
        showError(message || fallbackError);
      }

      // 5. cârligele de după adăugare (add-on-ul cardului personalizat), pe rând
      const detail = { form: form, quantity: added, item: partial ? null : data, partial: partial };
      const hooks = Array.isArray(Giftera.afterSetAdded) ? Giftera.afterSetAdded.slice() : [];
      for (const hook of hooks) {
        if (typeof hook !== 'function') continue;
        try {
          await hook(detail);
        } catch (error) {
          // un cârlig stricat nu oprește adăugarea setului
        }
      }
      document.dispatchEvent(new CustomEvent('gf:set-added', { detail: detail }));

      // 6. „Comandă acum”: direct la finalizarea comenzii. Add-on-ul a fost pus de cârlig cu cantitatea
      //    setului, deci coșul e deja la zi. Butoanele rămân dezactivate până pleacă pagina. La o adăugare
      //    parțială rămânem pe pagină, ca mesajul despre stoc să fie văzut (ca la „Adaugă în coș”). La fel
      //    când cârligul designerului nu a putut adăuga cardul personalizat (detail.addonFailed).
      if (buyNow && !partial && !detail.addonFailed) {
        Giftera.navigate(base + 'checkout');
        return;
      }

      setBusy(false);

      // 6. „Adaugă în coș”
      openCart(root.getAttribute('data-cart-url') || '');
    });
  }

  /* ---------------------------------------------------------------------- */
  /* cantitate                                                               */
  /* ---------------------------------------------------------------------- */

  /** @param {HTMLElement} root */
  function initQuantity(root) {
    const input = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-gf-qty]'));
    if (!input) return;

    const min = Math.max(1, parseInt(input.min, 10) || 1);

    function read() {
      const value = parseInt(input.value, 10);
      return Number.isFinite(value) && value >= min ? value : min;
    }

    /** @param {number} value */
    function write(value) {
      const max = parseInt(input.max, 10);
      let next = Math.max(min, value);
      if (Number.isFinite(max) && max >= min) next = Math.min(max, next);
      if (String(next) === input.value) return;
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const minus = root.querySelector('[data-gf-qty-minus]');
    const plus = root.querySelector('[data-gf-qty-plus]');
    if (minus) minus.addEventListener('click', function () { write(read() - 1); });
    if (plus) plus.addEventListener('click', function () { write(read() + 1); });
    input.addEventListener('blur', function () { input.value = String(read()); });
  }

  /* ---------------------------------------------------------------------- */
  /* galerie + zoom                                                          */
  /* ---------------------------------------------------------------------- */

  /**
   * @param {HTMLElement} root
   * @param {HTMLFormElement | null} form
   */
  function initGallery(root, form) {
    const gallery = root.querySelector('[data-gf-gallery]');
    if (!gallery) return;

    const main = /** @type {HTMLElement | null} */ (gallery.querySelector('[data-gf-gallery-main]'));
    const image = main ? /** @type {HTMLImageElement | null} */ (main.querySelector('img')) : null;
    const thumbs = /** @type {HTMLElement[]} */ (Array.from(gallery.querySelectorAll('[data-gf-thumb]')));
    if (!main || !image) return;

    /** @param {HTMLElement} thumb */
    function show(thumb) {
      const src = thumb.getAttribute('data-src');
      if (!src) return;
      const srcset = thumb.getAttribute('data-srcset');

      // srcset înaintea lui src, ca browserul să nu încarce de două ori
      if (srcset) image.setAttribute('srcset', srcset);
      else image.removeAttribute('srcset');
      image.src = src;
      image.alt = thumb.getAttribute('data-alt') || '';

      for (const other of thumbs) other.setAttribute('aria-current', other === thumb ? 'true' : 'false');
    }

    for (const thumb of thumbs) {
      thumb.addEventListener('click', function () { show(thumb); });
    }

    initVariantThumb(root, gallery, thumbs, show);
    initModelThumb(root, form, gallery, thumbs, show);
    initZoom(main, image);
  }

  /**
   * Seturile cu variante reale: o singură miniatură ([data-gf-variant-thumb]) ține poza variantei alese.
   * La `gf:variant-change` (emis de initVariants) își ia poza din datele variantei; cu `show`, devine și poza
   * principală. Fără miniatură (produs cu o singură variantă) nu face nimic.
   * @param {HTMLElement} root
   * @param {Element} gallery
   * @param {HTMLElement[]} thumbs
   * @param {(thumb: HTMLElement) => void} show
   */
  function initVariantThumb(root, gallery, thumbs, show) {
    const thumb = /** @type {HTMLElement | null} */ (gallery.querySelector('[data-gf-variant-thumb]'));
    if (!thumb) return;
    const thumbImage = thumb.querySelector('img');

    root.addEventListener('gf:variant-change', function (event) {
      const detail = /** @type {CustomEvent} */ (event).detail || {};
      const variant = detail.variant;
      if (!variant) return;

      // varianta nu are poză: miniatura dispare, iar poza principală trece pe prima rămasă
      if (!variant.src) {
        const wasCurrent = thumb.getAttribute('aria-current') === 'true';
        thumb.hidden = true;
        if (wasCurrent) {
          const first = thumbs.find(function (item) { return item !== thumb && !item.hidden; });
          if (first) show(first);
        }
        return;
      }

      // la încărcare miniatura are deja poza variantei (din Liquid): îi lipsește doar data-srcset
      const same = thumb.getAttribute('data-src') === variant.src;
      thumb.setAttribute('data-srcset', srcsetFor(variant.src, MAIN_WIDTHS, variant.width));
      if (!same) {
        const alt = variant.alt || variant.title || '';
        const label = thumb.getAttribute('data-label') || '';
        thumb.setAttribute('data-src', variant.src);
        thumb.setAttribute('data-alt', alt);
        if (label) thumb.setAttribute('aria-label', alt ? label + ': ' + alt : label);
        if (thumbImage) {
          const smallSet = srcsetFor(variant.src, THUMB_WIDTHS);
          if (smallSet) thumbImage.setAttribute('srcset', smallSet);
          else thumbImage.removeAttribute('srcset');
          thumbImage.src = atWidth(variant.src, THUMB_WIDTHS[THUMB_WIDTHS.length - 1]) || variant.src;
        }
      }
      thumb.hidden = false;
      if (detail.show) show(thumb);
    });
  }

  /**
   * Miniatura lănțișorului ales: se schimbă la `change` pe input[data-gf-value] (emis de selector).
   * @param {HTMLElement} root
   * @param {HTMLFormElement | null} form
   * @param {Element} gallery
   * @param {HTMLElement[]} thumbs
   * @param {(thumb: HTMLElement) => void} show
   */
  function initModelThumb(root, form, gallery, thumbs, show) {
    const thumb = /** @type {HTMLElement | null} */ (gallery.querySelector('[data-gf-model-thumb]'));
    if (!thumb) return;

    const thumbImage = thumb.querySelector('img');
    const mapEl = gallery.querySelector('[data-gf-model-images]');
    let images = {};
    try {
      images = mapEl ? JSON.parse(mapEl.textContent || '{}') : {};
    } catch (error) {
      images = {};
    }

    function hide() {
      const wasCurrent = thumb.getAttribute('aria-current') === 'true';
      thumb.hidden = true;
      if (wasCurrent) {
        const first = thumbs.find(function (item) { return item !== thumb && !item.hidden; });
        if (first) show(first);
      }
    }

    // fișierul gf-nk-nXX.webp lipsește din temă → fără miniatură
    if (thumbImage) {
      thumbImage.addEventListener('error', hide);
      thumbImage.addEventListener('load', function () { thumb.hidden = false; });
      if (thumbImage.complete && thumbImage.naturalWidth === 0 && thumbImage.currentSrc) hide();
    }

    function update() {
      const codeInput = /** @type {HTMLInputElement | null} */ (findControl(root, form, 'input[data-gf-code]'));
      const valueInput = /** @type {HTMLInputElement | null} */ (findControl(root, form, 'input[data-gf-value]'));
      const code = codeInput ? codeInput.value : '';
      const name = valueInput ? valueInput.value : '';
      const src = code ? images[code] : '';
      if (!src) return;

      const labelTemplate = thumb.getAttribute('data-label-template') || '';
      const altTemplate = thumb.getAttribute('data-alt-template') || '';
      thumb.setAttribute('data-src', src);
      if (name) {
        if (labelTemplate) thumb.setAttribute('aria-label', labelTemplate.replace('__NAME__', name));
        if (altTemplate) thumb.setAttribute('data-alt', altTemplate.replace('__NAME__', name));
      }
      if (thumbImage && thumbImage.getAttribute('src') !== src) thumbImage.src = src;
      if (thumb.getAttribute('aria-current') === 'true') show(thumb);
    }

    root.addEventListener('change', function (event) {
      const target = /** @type {Element} */ (event.target);
      if (target && typeof target.matches === 'function' && target.matches('input[data-gf-value], input[data-gf-code]')) update();
    });
  }

  /**
   * Zoom: doar pe dispozitive cu hover; scale 1.8 cu transform-origin după cursor.
   * @param {HTMLElement} main
   * @param {HTMLImageElement} image
   */
  function initZoom(main, image) {
    if (!main.hasAttribute('data-gf-zoom')) return;
    if (!window.matchMedia || !window.matchMedia('(hover: hover)').matches) return;

    main.classList.add('gf-gal__main--zoomable');

    main.addEventListener('mouseenter', function () {
      // la zoom poza e afișată de 1.8 ori mai mare: cerem din srcset o variantă pe măsură
      const width = Math.ceil(main.getBoundingClientRect().width * ZOOM_SCALE);
      if (width > 0 && image.hasAttribute('srcset')) image.sizes = width + 'px';
      main.classList.add('is-zoom');
    });

    main.addEventListener('mouseleave', function () {
      main.classList.remove('is-zoom');
      image.style.transformOrigin = '50% 50%';
    });

    main.addEventListener('mousemove', function (event) {
      const rect = main.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      image.style.transformOrigin = x + '% ' + y + '%';
    });
  }

  /* ---------------------------------------------------------------------- */
  /* bara lipicioasă                                                         */
  /* ---------------------------------------------------------------------- */

  /**
   * @param {HTMLElement} root
   * @param {HTMLFormElement | null} form
   */
  function initSticky(root, form) {
    const bar = /** @type {HTMLElement | null} */ (root.querySelector('[data-gf-sticky]'));
    const mainButton = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-gf-add]'));
    if (!bar || !mainButton) return;

    const barButton = /** @type {HTMLButtonElement | null} */ (bar.querySelector('[data-gf-sticky-add]'));
    const sub = bar.querySelector('[data-gf-sticky-sub]');

    /** @param {boolean} on */
    function toggle(on) {
      bar.classList.toggle('is-on', on);
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      bar.inert = !on;
      if (barButton) barButton.tabIndex = on ? 0 : -1;
      document.body.classList.toggle('has-stb', on);
    }

    function updateSub() {
      if (!sub) return;
      const valueInput = /** @type {HTMLInputElement | null} */ (findControl(root, form, 'input[data-gf-value]'));
      const customInput = /** @type {HTMLInputElement | null} */ (root.querySelector('input[data-gf-custom]:checked'));
      const isCustom = !!customInput && customInput.getAttribute('data-gf-custom') === 'true';
      const card = bar.getAttribute(isCustom ? 'data-label-custom' : 'data-label-ours') || '';
      const model = valueInput && valueInput.value ? valueInput.value : '';
      if (!model) return; // selectorul lipsește: rămâne textul din Liquid
      sub.textContent = card ? model + ' · ' + card : model;
    }

    if (barButton) {
      barButton.addEventListener('click', function () {
        if (!form || mainButton.disabled) return;
        if (typeof form.requestSubmit === 'function') form.requestSubmit(mainButton);
        else mainButton.click();
      });

      // starea butonului principal (se adaugă… / stoc epuizat) se vede și pe bară
      if ('MutationObserver' in window) {
        new MutationObserver(function () {
          barButton.disabled = mainButton.disabled;
          if (mainButton.getAttribute('aria-busy') === 'true') barButton.setAttribute('aria-busy', 'true');
          else barButton.removeAttribute('aria-busy');
        }).observe(mainButton, { attributes: true, attributeFilter: ['disabled', 'aria-busy'] });
      }
    }

    root.addEventListener('change', function (event) {
      const target = /** @type {Element} */ (event.target);
      if (target && typeof target.matches === 'function' && target.matches('input[data-gf-value], input[data-gf-code], input[data-gf-custom]')) updateSub();
    });
    updateSub();

    // activă cât butonul principal nu e în ecran: după ce a trecut de el (deasupra) și, de la deschiderea paginii,
    // cât butonul e încă sub ecran — pe telefon e la 2–3 ecrane, iar prețul și cumpărarea trebuie văzute de la început
    function sync() {
      const r = mainButton.getBoundingClientRect();
      toggle(r.bottom <= 0 || r.top >= window.innerHeight);
    }

    // Observerul anunță doar trecerile butonului prin ecran; la un salt instant peste el (Home / End,
    // butonul „sus” cu mișcare redusă) nu apare nicio trecere, deci starea se recalculează și la derulare.
    let ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        sync();
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // starea de pornire imediat (observerul poate întârzia, de exemplu cât fila nu e în prim-plan)
    sync();
    if ('IntersectionObserver' in window) new IntersectionObserver(sync, { threshold: 0 }).observe(mainButton);
  }

  /* ---------------------------------------------------------------------- */
  /* blocul „Cardul: mesajul nostru sau al tău” (selectorul de card)         */
  /* ---------------------------------------------------------------------- */

  /**
   * Cardurile-exemplu și butonul „Personalizează cardul” (snippets/gf-set-options.liquid, part: 'card').
   * Starea rămâne în cele două radio-uri ascunse `input[data-gf-custom]`: aici doar se bifează radio-ul și
   * se emite `change`, pe care îl ascultă designerul (apare / dispare), bara lipicioasă și „Ce găsește…”.
   * Cardul din dreapta și butonul derulează la panoul „Designul tău pentru card”; butonul îi dă și focusul.
   * @param {HTMLElement} root
   */
  function initCardChoice(root) {
    const block = /** @type {HTMLElement | null} */ (root.querySelector('[data-gf-card-choice]'));
    if (!block) return;
    const picks = /** @type {HTMLButtonElement[]} */ (Array.from(block.querySelectorAll('[data-gf-card-pick]')));
    const radios = /** @type {HTMLInputElement[]} */ (Array.from(block.querySelectorAll('input[data-gf-custom]')));
    if (!picks.length || radios.length < 2) return;

    // cu JS, butoanele sunt controalele; radio-urile rămân doar stare
    for (const radio of radios) {
      radio.tabIndex = -1;
      radio.setAttribute('aria-hidden', 'true');
    }

    function isCustom() {
      const checked = radios.find((radio) => radio.checked);
      return !!checked && checked.getAttribute('data-gf-custom') === 'true';
    }

    function sync() {
      const custom = isCustom();
      for (const pick of picks) {
        if (!pick.hasAttribute('aria-pressed')) continue;
        pick.setAttribute('aria-pressed', (pick.getAttribute('data-gf-card-pick') === 'true') === custom ? 'true' : 'false');
      }
    }

    /** @param {boolean} focus */
    function reveal(focus) {
      const panel = /** @type {HTMLElement | null} */ (root.querySelector('gf-card-designer'));
      if (!panel || panel.hidden) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (focus) panel.focus({ preventScroll: true });
      panel.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }

    for (const pick of picks) {
      pick.addEventListener('click', function () {
        const custom = pick.getAttribute('data-gf-card-pick') === 'true';
        const radio = radios.find((item) => (item.getAttribute('data-gf-custom') === 'true') === custom);
        if (!radio) return;
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        sync();
        if (custom) reveal(pick.hasAttribute('data-gf-card-go'));
      });
    }

    block.addEventListener('change', function (event) {
      const target = /** @type {Element} */ (event.target);
      if (target && typeof target.matches === 'function' && target.matches('input[data-gf-custom]')) sync();
    });
    // starea radio-urilor poate fi restaurată de browser (reîncărcare, înapoi)
    window.addEventListener('pageshow', sync);
    sync();
  }

  /* ---------------------------------------------------------------------- */
  /* seturile cu variante reale („Model colier”)                             */
  /* ---------------------------------------------------------------------- */

  /**
   * Selectorul de lănțișor scrie, ca până acum, „Model lănțișor” (input[data-gf-value]) și „_Cod lănțișor”
   * (input[data-gf-code]) și emite `change`. La seturile cu variante reale, secțiunea aduce și lista
   * variantelor după cod (<script data-gf-variants>, din sections/giftera-product.liquid): aici codul ales
   * devine varianta din formular (`id`) și se țin la zi titlul (h1 + document.title), poza, adresa
   * (?variant=), poza barei lipicioase și linkul de trimis.
   * Fără <script data-gf-variants> (set cu o singură variantă) nu face nimic.
   * @param {HTMLElement} root
   * @param {HTMLFormElement | null} form
   */
  function initVariants(root, form) {
    const mapEl = root.querySelector('script[data-gf-variants]');
    if (!mapEl || !form) return;
    /** @type {Record<string, { id: number, name: string, title: string, src?: string, width?: number, alt?: string }>} */
    let variants = {};
    try {
      variants = JSON.parse(mapEl.textContent || '{}') || {};
    } catch (error) {
      variants = {};
    }

    const idInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
    const errorEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-gf-form-error]'));
    const live = root.querySelector('[data-gf-model-live]');
    const note = /** @type {HTMLElement | null} */ (root.querySelector('[data-gf-model-note]'));
    const share = root.querySelector('[data-gf-share]');
    const stickyImage = /** @type {HTMLImageElement | null} */ (root.querySelector('[data-gf-sticky] .gf-stb__img'));
    const titles = Array.from(root.querySelectorAll('[data-gf-title]'));
    if (!idInput) return;

    function codeInput() {
      return /** @type {HTMLInputElement | null} */ (findControl(root, form, 'input[data-gf-code]'));
    }
    function valueInput() {
      return /** @type {HTMLInputElement | null} */ (findControl(root, form, 'input[data-gf-value]'));
    }

    /** Varianta care corespunde codului din „_Cod lănțișor”; null dacă nu există una. */
    function current() {
      const input = codeInput();
      const code = input ? input.value : '';
      return code && Object.prototype.hasOwnProperty.call(variants, code) ? { code: code, variant: variants[code] } : null;
    }

    // titlul randat de server: după el se găsește (și se înlocuiește) titlul variantei în document.title
    let shownTitle = titles.length ? (titles[0].textContent || '').trim() : '';
    /** Codul plăcuței bifate în selector (ce vede omul); '' dacă selectorul lipsește. */
    function checkedCode() {
      const tile = root.querySelector('.gf-mp__tile[aria-checked="true"]');
      return tile ? tile.getAttribute('data-code') || '' : '';
    }

    // codul variantei afișate: la început cea randată de server; cât rămâne aceeași, pozele nu se ating
    const firstCode = codeInput();
    let shownCode = checkedCode() || (firstCode ? firstCode.value : '');

    /**
     * @param {{ code: string, variant: any }} picked
     * @param {boolean} byUser - true = alegere făcută acum (schimbă și poza principală, adresa și anunță)
     */
    function apply(picked, byUser) {
      const variant = picked.variant;
      const id = String(variant.id);
      const changed = shownCode !== picked.code;
      shownCode = picked.code;
      if (idInput.value !== id) idInput.value = id;

      if (variant.title) {
        for (const el of titles) {
          if (el.textContent !== variant.title) el.textContent = variant.title;
        }
        // un titlu SEO scris de mână nu conține titlul variantei și rămâne neatins
        if (shownTitle && shownTitle !== variant.title && document.title.indexOf(shownTitle) !== -1) {
          document.title = document.title.split(shownTitle).join(variant.title);
        }
        shownTitle = variant.title;
        if (share) share.setAttribute('data-title', variant.title);
      }

      if (changed && stickyImage && variant.src) {
        const smallSet = srcsetFor(variant.src, STICKY_WIDTHS);
        if (smallSet) stickyImage.setAttribute('srcset', smallSet);
        else stickyImage.removeAttribute('srcset');
        stickyImage.src = atWidth(variant.src, STICKY_WIDTHS[STICKY_WIDTHS.length - 1]) || variant.src;
      }

      root.dispatchEvent(new CustomEvent('gf:variant-change', { detail: { code: picked.code, variant: variant, show: byUser } }));
      if (!byUser) return;

      // adresa paginii și linkul de trimis duc la modelul ales; restul parametrilor rămân
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', id);
        window.history.replaceState(window.history.state, '', url.toString());
        if (share) {
          const shareUrl = new URL(share.getAttribute('data-url') || url.toString(), window.location.href);
          shareUrl.searchParams.set('variant', id);
          share.setAttribute('data-url', shareUrl.toString());
        }
      } catch (error) {
        // adresa rămâne cum e
      }

      if (note) note.hidden = true; // „modelul cerut nu e în stoc” nu mai e de actualitate
      if (live) {
        const template = live.getAttribute('data-template') || '';
        live.textContent = template ? template.split('__NAME__').join(variant.name || '') : variant.name || '';
      }
    }

    /**
     * La încărcare și la revenirea pe pagină: adevărul e ce vede omul, adică plăcuța bifată din selector.
     * Dacă inputurile au rămas la altă valoare (readuse de browser), se aliniază la ea, apoi `id` la cod.
     */
    function resync() {
      const code = checkedCode();
      const codeEl = codeInput();
      const valueEl = valueInput();
      if (code && codeEl && codeEl.value !== code && Object.prototype.hasOwnProperty.call(variants, code)) {
        codeEl.value = code;
        if (valueEl) valueEl.value = variants[code].name || '';
      }
      const picked = current();
      if (picked) apply(picked, false);
    }

    root.addEventListener('change', function (event) {
      const target = /** @type {Element} */ (event.target);
      if (!target || typeof target.matches !== 'function' || !target.matches('input[data-gf-value], input[data-gf-code]')) return;
      const picked = current();
      if (picked) apply(picked, true);
    });

    // Garda dinaintea adăugării în coș, în faza de captură pe secțiune: rulează înaintea designerului (care ar
    // urca poza) și a trimiterii. `id` se recalculează din „_Cod lănțișor”; dacă nu se potrivesc cu nicio
    // variantă, nu pleacă nimic și apare mesajul.
    root.addEventListener('submit', function (event) {
      // fără selector pe pagină (blocul „Model lănțișor” scos din șablon) nu e nimic de comparat
      if (event.target !== form || !codeInput()) return;
      const picked = current();
      if (!picked) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (errorEl) {
          errorEl.textContent = root.getAttribute('data-model-error') || root.getAttribute('data-error-text') || '';
          errorEl.hidden = !errorEl.textContent;
        }
        return;
      }
      const id = String(picked.variant.id);
      if (idInput.value !== id) idInput.value = id;
      const valueEl = valueInput();
      if (valueEl && picked.variant.name && valueEl.value !== picked.variant.name) valueEl.value = picked.variant.name;
    }, true);

    window.addEventListener('pageshow', resync);
    resync();
  }

  /* ---------------------------------------------------------------------- */
  /* „Trimite”                                                               */
  /* ---------------------------------------------------------------------- */

  /**
   * @param {string} text
   * @returns {Promise<boolean>}
   */
  async function copyText(text) {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        // încercăm varianta veche
      }
    }

    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.top = '0';
    field.style.left = '0';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (error) {
      copied = false;
    }
    field.remove();
    return copied;
  }

  /** @param {HTMLElement} root */
  function initShare(root) {
    const button = /** @type {HTMLElement | null} */ (root.querySelector('[data-gf-share]'));
    if (!button) return;

    button.addEventListener('click', async function () {
      const url = button.getAttribute('data-url') || window.location.href;
      const title = button.getAttribute('data-title') || document.title;

      if (typeof navigator.share === 'function') {
        try {
          await navigator.share({ title: title, url: url });
          return;
        } catch (error) {
          if (error && error.name === 'AbortError') return; // utilizatorul a închis foaia de partajare
        }
      }

      if (await copyText(url)) toast(button.getAttribute('data-copied') || '');
      button.focus({ preventScroll: true });
    });
  }

  /* ---------------------------------------------------------------------- */
  /* pornire                                                                 */
  /* ---------------------------------------------------------------------- */

  /** @param {ParentNode} [scope] */
  function init(scope) {
    const roots = /** @type {HTMLElement[]} */ (Array.from((scope || document).querySelectorAll('[data-gf-product]')));
    for (const root of roots) {
      if (initialised.has(root)) continue;
      initialised.add(root);

      const form = /** @type {HTMLFormElement | null} */ (root.querySelector('form[data-gf-product-form]'));
      if (form) initForm(root, form);
      initQuantity(root);
      initGallery(root, form);
      initVariants(root, form);
      initCardChoice(root);
      initSticky(root, form);
      initShare(root);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  // editorul de temă: secțiunea e reîncărcată
  document.addEventListener('shopify:section:load', function (event) {
    init(/** @type {ParentNode} */ (event.target));
  });
  document.addEventListener('shopify:section:unload', function (event) {
    const target = /** @type {Element} */ (event.target);
    if (target && target.querySelector && target.querySelector('[data-gf-product]')) document.body.classList.remove('has-stb');
  });
})();
